import { useCallback, useState } from "react";

import { isFalsy } from "@vega-core/utils/isFalsy";

type Updater<T> = T | ((prev: T) => T);

const isUpdaterFn = <T>(update: Updater<T>): update is (prev: T) => T =>
  typeof update === "function";

export function useSessionStorage<T = unknown>(
  keyName: string,
  defaultValue: T,
): [value: T, setValue: (update: Updater<T>) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const value = sessionStorage.getItem(keyName);

      if (!isFalsy(value)) {
        return JSON.parse(value) as T;
      } else {
        sessionStorage.setItem(keyName, JSON.stringify(defaultValue));

        return defaultValue;
      }
    } catch (err) {
      console.error(err);

      return defaultValue;
    }
  });

  const setValue = useCallback(
    (update: Updater<T>) => {
      setStoredValue((prev) => {
        const next = isUpdaterFn(update) ? update(prev) : update;
        sessionStorage.setItem(keyName, JSON.stringify(next));

        return next;
      });
    },
    [keyName],
  );

  return [storedValue, setValue];
}
